import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { AISearchBar } from '../components/dashboard/AISearchBar';
import { QuickStats } from '../components/dashboard/QuickStats';
import { DashboardCard } from '../components/dashboard/DashboardCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, TrendingUp, Users, Eye, Heart, ArrowUpRight, Sparkles } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [quickStats, setQuickStats] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [overview, setOverview] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [topCreatives, setTopCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, dashboardsRes, overviewRes, trendRes, creativesRes] = await Promise.all([
          axios.get(`${API}/quick-stats`),
          axios.get(`${API}/dashboards`),
          axios.get(`${API}/analytics/overview`),
          axios.get(`${API}/analytics/monthly-trend`),
          axios.get(`${API}/analytics/creatives`)
        ]);
        setQuickStats(statsRes.data);
        setDashboards(dashboardsRes.data);
        setOverview(overviewRes.data);
        setTrendData(trendRes.data);
        setTopCreatives(creativesRes.data.top_performers || []);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <Layout>
      <div className="space-y-8" data-testid="home-page">
        {/* Quick Stats */}
        <div className="flex justify-start">
          <QuickStats stats={quickStats} />
        </div>

        {/* Welcome Section */}
        <div className="text-center py-8 space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800" data-testid="welcome-title">
            Welcome back, <span className="gradient-text">Giri UK</span>
          </h1>
          <p className="text-lg text-slate-500" data-testid="welcome-subtitle">
            What would you like to see today?
          </p>
          <AISearchBar />
        </div>

        {/* KPI Overview Cards */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-overview">
            {overview.kpis.map((kpi, index) => {
              const icons = [Eye, Users, TrendingUp, Heart];
              const colors = ['purple', 'pink', 'blue', 'emerald'];
              const IconComponent = icons[index];
              return (
                <Card key={kpi.label} className="bg-white shadow-sm card-hover cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className={`p-2 bg-${colors[index]}-100 rounded-lg group-hover:scale-110 transition-transform`}>
                        <IconComponent className={`h-5 w-5 text-${colors[index]}-600`} />
                      </div>
                      <Badge variant="secondary" className={kpi.changeType === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {kpi.change}
                      </Badge>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-slate-500">{kpi.label}</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{kpi.value}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Performance Trend Chart */}
        {trendData.length > 0 && (
          <Card className="bg-white shadow-sm" data-testid="trend-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-800">Performance Trend</CardTitle>
                <Badge variant="outline" className="text-purple-600 border-purple-200">Last 4 Months</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorReach" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EC4899" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#EC4899" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value, name) => [value.toLocaleString(), name === 'total_reach' ? 'Total Reach' : 'Engagement']}
                    />
                    <Area type="monotone" dataKey="total_reach" stroke="#8B5CF6" strokeWidth={2} fill="url(#colorReach)" />
                    <Area type="monotone" dataKey="engagement" stroke="#EC4899" strokeWidth={2} fill="url(#colorFollowers)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Performing Creatives */}
        {topCreatives.length > 0 && (
          <div data-testid="top-creatives">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                Top Performing Creatives
              </h2>
              <Button variant="link" className="text-purple-600">View All →</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {topCreatives.map((creative, index) => (
                <Card 
                  key={creative.id} 
                  className="bg-white shadow-sm overflow-hidden group cursor-pointer hover:shadow-lg transition-all"
                  data-testid={`creative-${creative.id}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img 
                      src={creative.thumbnail} 
                      alt={creative.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-white text-xs font-medium truncate">{creative.title}</p>
                      </div>
                    </div>
                    {index === 0 && (
                      <Badge className="absolute top-2 left-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                        #1 Top
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">{creative.type}</Badge>
                      <span className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {creative.engagement.toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* My Dashboards Section */}
        <div className="rounded-2xl overflow-hidden" data-testid="dashboards-section">
          <div className="gradient-btn px-6 py-4 flex items-center justify-between relative z-10">
            <h2 className="text-lg font-semibold text-white">My Dashboards</h2>
            <Button variant="ghost" className="text-white hover:bg-white/20 relative z-20" data-testid="create-dashboard-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create a Dashboard
            </Button>
          </div>
          <div className="bg-white p-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-slate-100 rounded-lg h-40"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {dashboards.map((dashboard, index) => (
                  <DashboardCard
                    key={dashboard.id}
                    name={dashboard.name}
                    description={dashboard.description}
                    thumbnail={index === 0 ? "https://images.unsplash.com/photo-1726156434753-7191678ca099?w=400&h=300&fit=crop" : null}
                    isActive={index === 0}
                  />
                ))}
              </div>
            )}
            <div className="text-right mt-4">
              <Button variant="link" className="text-slate-500 hover:text-slate-700" data-testid="view-all-btn">
                View all
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
